import React, { FC, useEffect, useState } from 'react';
import { useMediaPredicate } from 'react-media-hook';
import { Col, Layout, Row } from 'antd';
import moment from 'moment';
import { PageContent } from 'common/components/Page/PageContent';
import { PageHeader } from 'common/components/Page/PageHeader';
import { PageHeaderMob } from 'common/components/Page/PageHeaderMob';
import { PageStoreSelector } from 'common/components/PageStoreSelector';
import { PeriodFilterCard } from 'common/components/PeriodFilterCard';
import { PeriodFilterToolbar } from 'common/components/PeriodFilterToolbar';
import { MEDIA_MOBILE_768 } from 'common/const/common.const';
import { ESidebarMenuKey, ESidebarMenuLabel, sidebarMenuIcons } from 'common/models/commonModel';
import { getFilterWeek } from 'common/helpers/date.helper';
import { usePeriodFilter } from 'common/hooks/usePeriodFilter';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getMonitorSalesStoresCollection } from 'app/store/reducers/monitorSales.reducer';
import { SalesSummaryBlock } from 'entities/MonitorSales/components/SalesSummaryBlock';
import { CategorySalesBlock } from 'entities/MonitorSales/components/CategorySalesBlock';
import { ProductSalesBlock } from 'entities/MonitorSales/components/ProductSalesBlock';
import { ProductRatioBlock } from 'entities/MonitorSales/components/ProductRatioBlock';
import { OosOpportunityLossBlock } from 'entities/MonitorSales/components/OosOpportunityLossBlock';
import { SalesHeatmapBlock } from 'entities/MonitorSales/components/SalesHeatmapBlock';

export const MonitorSalesPage: FC = () => {
  const dispatch = useAppDispatch();

  const { monitorSalesStoresCollection } = useAppSelector((state) => state.monitorSales);

  const isMobile: boolean = useMediaPredicate(MEDIA_MOBILE_768);

  const [storeCode, setStoreCode] = useState<string | number | undefined>(undefined);

  const periodFilter = usePeriodFilter();

  const resolvedWeek = periodFilter.week ?? moment().isoWeek();
  const resolvedYear = periodFilter.year ?? moment().isoWeekYear();
  const weekStart = moment().isoWeekYear(resolvedYear).isoWeek(resolvedWeek).startOf('isoWeek');
  const weekEnd = weekStart.clone().endOf('isoWeek');
  const coverage = `${weekStart.format('MMM D, YYYY')} to ${weekEnd.format('MMM D, YYYY')} (12:00 am — 11:59 pm)`;
  // Default = current week, sent explicitly so the BE window always matches the coverage label.
  const filterWeek = getFilterWeek(resolvedYear, resolvedWeek);

  useEffect(() => {
    dispatch(getMonitorSalesStoresCollection());
  }, []);

  const changeStore = (value?: string | number) => {
    setStoreCode(value);
  };

  const headerFilters = (
    <Row gutter={[8, 8]} align="middle">
      <Col style={{ maxWidth: 149 }}>
        <PageStoreSelector stores={monitorSalesStoresCollection?.data} onChange={changeStore} allowAll />
      </Col>
      <Col>
        <PeriodFilterToolbar
          year={periodFilter.year}
          week={periodFilter.week}
          filterActive={periodFilter.open || !!storeCode}
          onWeekSelect={periodFilter.onWeekSelect}
          onOpen={periodFilter.openFilterCard}
        />
      </Col>
    </Row>
  );

  return (
    <Layout className="relative height-full flex flex__column">
      {isMobile ? (
        <PageHeaderMob
          title={
            <div className="mb-3 flex flex-align-center">
              {sidebarMenuIcons[ESidebarMenuKey.MonitorSales]}
              <span className="ml-3 ff-regular">{ESidebarMenuLabel[ESidebarMenuKey.MonitorSales]}</span>
            </div>
          }
          extra={[<span key="filters">{headerFilters}</span>]}
        />
      ) : (
        <PageHeader
          title={
            <div>
              <span>{ESidebarMenuLabel[ESidebarMenuKey.MonitorSales]}</span>
            </div>
          }
          extra={headerFilters}
        />
      )}

      <PageContent>
        <div
          className={`page-content__container page-content__scroll-container ${
            isMobile ? 'scrollbar-none' : 'scrollbar-x'
          }`}
        >
          <Row gutter={16}>
            <Col span={16}>
              <Row className="mb-5">
                <Col span={24}>
                  <SalesSummaryBlock
                    dateRange={coverage}
                    params={{
                      storeCode: storeCode !== undefined ? String(storeCode) : undefined,
                      week: filterWeek,
                    }}
                  />
                </Col>
              </Row>
              <Row gutter={[16, 16]}>
                <Col span={24} xxl={14}>
                  <Row gutter={[12, 12]} align="stretch">
                    <Col span={24}>
                      <CategorySalesBlock
                        params={{
                          storeCode: storeCode !== undefined ? String(storeCode) : undefined,
                          week: filterWeek,
                        }}
                      />
                    </Col>
                    <Col span={24}>
                      <ProductSalesBlock
                        params={{
                          storeCode: storeCode !== undefined ? String(storeCode) : undefined,
                          week: filterWeek,
                        }}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col span={24} xxl={10}>
                  <Row gutter={[16, 16]} align="stretch">
                    <Col span={24}>
                      <ProductRatioBlock
                        params={{
                          storeCode: storeCode !== undefined ? String(storeCode) : undefined,
                          week: filterWeek,
                        }}
                      />
                    </Col>
                    <Col span={24}>
                      <OosOpportunityLossBlock
                        params={{
                          storeCode: storeCode !== undefined ? String(storeCode) : undefined,
                          week: filterWeek,
                        }}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>

            <Col span={8}>
              <SalesHeatmapBlock
                params={{
                  storeCode: storeCode !== undefined ? String(storeCode) : undefined,
                  week: filterWeek,
                }}
              />
            </Col>
          </Row>
        </div>

        <PeriodFilterCard
          open={periodFilter.open}
          year={periodFilter.year}
          week={periodFilter.week}
          onYearChange={periodFilter.changeYear}
          onWeekChange={periodFilter.changeWeek}
          onCancel={periodFilter.closeFilterCard}
        />
      </PageContent>
    </Layout>
  );
};
